/**
 * API Call Tracking Utility
 * 
 * This utility provides functions to track API calls as either manual (user-initiated)
 * or automatic (system-initiated) for better user behavior analytics.
 */

/**
 * Call types for API tracking
 */
export const CALL_TYPES = {
  MANUAL: 'manual',
  AUTOMATIC: 'automatic'
};

/**
 * Creates headers with call type tracking information
 * @param {string} callType - The type of call (CALL_TYPES.MANUAL or CALL_TYPES.AUTOMATIC)
 * @param {Object} additionalHeaders - Any additional headers to include
 * @returns {Object} Headers object with tracking information
 */
export const createTrackedHeaders = (callType = CALL_TYPES.AUTOMATIC, additionalHeaders = {}) => {
  return {
    'X-Call-Type': callType,
    ...additionalHeaders
  };
};

/**
 * Enhanced fetch function with automatic call type tracking
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {string} callType - The type of call (defaults to AUTOMATIC)
 * @returns {Promise<Response>} Fetch response
 */
export const trackedFetch = async (url, options = {}, callType = CALL_TYPES.AUTOMATIC) => {
  const trackedOptions = {
    ...options,
    headers: {
      ...options.headers,
      ...createTrackedHeaders(callType)
    }
  };

  return fetch(url, trackedOptions);
};

/**
 * Creates a tracked fetch function with a specific call type
 * @param {string} callType - The default call type for this fetch function
 * @returns {Function} A fetch function that defaults to the specified call type
 */
export const createTrackedFetcher = (callType) => {
  return (url, options = {}) => trackedFetch(url, options, callType);
};

/**
 * Pre-configured fetch functions for common use cases
 */
export const manualFetch = createTrackedFetcher(CALL_TYPES.MANUAL);
export const automaticFetch = createTrackedFetcher(CALL_TYPES.AUTOMATIC);

/**
 * Wrapper for manual API calls (user-initiated actions)
 * Use this for button clicks, form submissions, user search, etc.
 * 
 * @param {Function} apiFunction - The API function to wrap
 * @returns {Function} Wrapped function that adds manual tracking
 */
export const withManualTracking = (apiFunction) => {
  return async (...args) => {
    // If the last argument is an options object, modify it to include manual tracking
    if (args.length > 0 && typeof args[args.length - 1] === 'object' && args[args.length - 1] !== null) {
      const lastArg = args[args.length - 1];
      if ('headers' in lastArg || 'method' in lastArg || 'body' in lastArg) {
        // This looks like a fetch options object
        lastArg.headers = {
          ...lastArg.headers,
          ...createTrackedHeaders(CALL_TYPES.MANUAL)
        };
      }
    }
    
    return apiFunction(...args);
  };
};

/**
 * Wrapper for automatic API calls (system-initiated)
 * Use this for page load data fetching, auto-refresh, polling, etc.
 * 
 * @param {Function} apiFunction - The API function to wrap
 * @returns {Function} Wrapped function that adds automatic tracking
 */
export const withAutomaticTracking = (apiFunction) => {
  return async (...args) => {
    // If the last argument is an options object, modify it to include automatic tracking
    if (args.length > 0 && typeof args[args.length - 1] === 'object' && args[args.length - 1] !== null) {
      const lastArg = args[args.length - 1];
      if ('headers' in lastArg || 'method' in lastArg || 'body' in lastArg) {
        // This looks like a fetch options object
        lastArg.headers = {
          ...lastArg.headers,
          ...createTrackedHeaders(CALL_TYPES.AUTOMATIC)
        };
      }
    }
    
    return apiFunction(...args);
  };
};

/**
 * Utility to add tracking headers to an existing request options object
 * @param {Object} options - Existing fetch options
 * @param {string} callType - The call type to add
 * @returns {Object} Modified options with tracking headers
 */
export const addTrackingToOptions = (options = {}, callType = CALL_TYPES.AUTOMATIC) => {
  return {
    ...options,
    headers: {
      ...options.headers,
      ...createTrackedHeaders(callType)
    }
  };
};

/**
 * Helper function to determine if a call should be marked as manual based on context
 * @param {Event} event - The event that triggered the call (optional)
 * @param {string} context - Additional context about the call
 * @returns {string} Appropriate call type
 */
export const inferCallType = (event = null, context = '') => {
  // If we have an event and it's a user interaction, mark as manual
  if (event && (
    event.type === 'click' ||
    event.type === 'submit' ||
    event.type === 'change' ||
    event.type === 'keypress' ||
    event.type === 'keydown'
  )) {
    return CALL_TYPES.MANUAL;
  }

  // Check context for manual indicators
  const manualKeywords = ['button', 'click', 'submit', 'search', 'filter', 'refresh', 'save', 'delete', 'update'];
  if (manualKeywords.some(keyword => context.toLowerCase().includes(keyword))) {
    return CALL_TYPES.MANUAL;
  }

  // Default to automatic
  return CALL_TYPES.AUTOMATIC;
};

/**
 * Enhanced service wrapper that automatically determines call type based on method names
 * @param {Object} service - Service object to wrap
 * @param {Object} methodConfig - Configuration for which methods should be manual vs automatic
 * @returns {Object} Wrapped service with automatic tracking
 */
export const wrapServiceWithTracking = (service, methodConfig = {}) => {
  const wrappedService = {};
  
  // Default patterns for manual methods
  const defaultManualPatterns = [
    /create/i, /update/i, /delete/i, /submit/i, /save/i, /search/i, 
    /filter/i, /refresh/i, /upload/i, /download/i, /reset/i
  ];
  
  // Default patterns for automatic methods
  const defaultAutomaticPatterns = [
    /get/i, /list/i, /fetch/i, /load/i, /check/i, /health/i, /status/i
  ];

  Object.keys(service).forEach(methodName => {
    if (typeof service[methodName] === 'function') {
      let callType = CALL_TYPES.AUTOMATIC;
      
      // Check explicit configuration first
      if (methodConfig[methodName]) {
        callType = methodConfig[methodName];
      } else {
        // Use pattern matching
        if (defaultManualPatterns.some(pattern => pattern.test(methodName))) {
          callType = CALL_TYPES.MANUAL;
        } else if (defaultAutomaticPatterns.some(pattern => pattern.test(methodName))) {
          callType = CALL_TYPES.AUTOMATIC;
        }
      }
      
      // Wrap the method
      if (callType === CALL_TYPES.MANUAL) {
        wrappedService[methodName] = withManualTracking(service[methodName].bind(service));
      } else {
        wrappedService[methodName] = withAutomaticTracking(service[methodName].bind(service));
      }
    } else {
      // Copy non-function properties as-is
      wrappedService[methodName] = service[methodName];
    }
  });
  
  return wrappedService;
};